<template>
    <div v-on:isInView="onItemIsInView" :style="computedStyles" v-show="computedStyles.marginTop" :data-aspect="item.aspect" class="gallery-item" :class="{ 'focused-is-in-view': hasEverBeenFocused, 'item--img' : item.isVideo == false, 'item--video' : item.isVideo, 'focus-visible': galleryVersion == 'legacy', 'img-loaded': imageLoaded, 'is-in-view': isInView, 'to-be-shown': item.visible }" role="listitem" tabindex="0" :data-id="item.id" aria-haspopup="dialog" @focus="onFocus" @blur="onBlur" @click="onItemClick(item)" @keydown.enter="onItemClick(item)">
        <div class="gallery-item__inner">
            <template v-if="item.isVideo">
                <gallery-video :item="item" mode="thumbnail" :is-parent-focused="hasFocus" v-bind:key="`video-${item.id}`" />
            </template>
            <template v-else>
                <div>
                    <picture data-type="list">
                        <source media="(min-width: 1800px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('> 1800')}`" />
                        <source media="(min-width: 1440px) and (max-width: 1799px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('> 1440 && < 1799')}`" />
                        <source media="(min-width: 1200px) and (max-width: 1439px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('> 1200 && < 1439')}`" />
                        <source media="(min-width: 992px) and (max-width: 1199px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('> 992 && < 1199')}`" />
                        <source media="(min-width: 768px) and (max-width: 991px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('> 768px && < 991')}`" />
                        <source media="(min-width: 540px) and (max-width: 767px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('> 540 && < 767 x1')} 1x, ${listingImageUrl}?width=${getImageSrcSetSize('> 540 && < 767 x2')} 2x`" />
                        <source media="(max-width: 539px)" :data-srcset-observer="`${listingImageUrl}?width=${getImageSrcSetSize('< 539 x1')} 1x, ${listingImageUrl}?width=${getImageSrcSetSize('< 539 x2')} 2x`" />
                        <img v-on:imgNowLoaded="onImageLoaded" class="asset-img" src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" :data-src-observer="`${listingImageUrl}?width=600`" :width="item.imageWidth" :height="item.imageHeight" :alt="item.imageAlternativeText" :aria-label="item.imageAlternativeText" />
                    </picture>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
    import galleryVideo from './galleryVideo.vue';
    import { getImageSrcSetSizeBasedOnGallery, preventPageScrolling } from '../mixins/helpers';
   
    export default {
        name: 'GalleryItem',
        props: ['item'],
        data() {
            return {
                hasFocus: false,
                hasEverBeenFocused: false,
                imageLoaded: false,
                isInView: false,
            }
        },

        computed: {
            ...mapState('gallery', [
                'galleryVersion',
                'layoutMode',
                'modalAnimationLength',
                'modalAnimating',
            ]),
            
            computedStyles() {
                if (this.item.width) {
                    return {
                        width: this.item.width,
                        aspectRatio: this.item.aspectRatio,
                        ['-webit-aspect-ratio']: this.item.aspectRatio,
                        left: this.item.left,
                        marginTop: this.item.marginTop,
                    };
                } else {
                    return {};
                }
            },
            getDisplayCategories() {
                return this.item.categories.join(', ');
            },

            listingImageUrl() {
                return (this.item.thumbnailImage && this.layoutMode == 'grid' ? this.item.thumbnailImage : this.item.image);
            }
        },

        mounted() {
        },

        methods: {
            ...mapMutations('gallery', [
                'updateModalOpened',
                'updateActiveItem',
                'updateModalAnimating',
            ]),
            ...mapActions('gallery', [
                'toggleModernModal',
            ]),

            onFocus() {
                this.hasFocus = true;
                this.hasEverBeenFocused = true;
            },
            onBlur() {
                setTimeout(() => {
                    this.hasFocus = false;
                }, 50);
            },

            onItemClick(item) {
                this.updateActiveItem(item);

                document.body.dataset.storedPosition = `-${window.scrollY}px`;

                if (this.modalAnimating) return false; // If we are already opening an item, don't rerun

                // Prevent the user from scrolling now the page to avoid issues with calculating the modal open position
                preventPageScrolling();

                // Update state to show we are opening modal and set the clicked item as the first to show in carousel
                this.updateModalOpened(true);
                this.updateModalAnimating(true);

                // When the modal CSS transition finsihes update state
                setTimeout(() => {
                    this.updateModalAnimating(false);
                }, this.modalAnimationLength)

                // Prevent page scrolling underneath modal
                if (this.galleryVersion === 'legacy' && window.innerWidth < 768) {
                    document.body.style.top = `-${window.scrollY}px`;
                    // document.body.dataset.storedPosition = `-${window.scrollY}px`;

                    setTimeout(() => {
                        document.body.style.position = 'fixed';
                        document.body.classList.add('gallery-modal-open');
                        document.body.classList.remove('gallery-modal-close');
                    }, 5);
                } else if (this.galleryVersion === 'modern') {
                    // Store the current scroll position so we can later scroll back to location when modal closes
                    document.body.dataset.storedPosition = `-${window.scrollY}px`;

                    // To fix issue with the footer occluding the modal, we need to increase zindex of main container once the modal has begun to be shown
                    setTimeout(() => {
                        const mainContent = document.querySelector('main.main-content');
                        if (mainContent) {
                            mainContent.classList.add('js-elevate-zindex');
                        }
                    }, 150);

                    this.toggleModernModal(true);

                } else {
                    document.documentElement.classList.add('overflow-hidden', 'video-fullscreened', 'gallery-modal-open');
                    document.body.classList.remove('gallery-modal-close');
                }
            },

            onImageLoaded() {
                this.imageLoaded = true;
            },

            onItemIsInView() {
                this.isInView = true;
            },

            getImageSrcSetSize(viewportRule) {
                return getImageSrcSetSizeBasedOnGallery(viewportRule, this.galleryVersion);
            },
        },

        components: {
            galleryVideo
        }
    }



</script>